const SAML_MD_NS = 'urn:oasis:names:tc:SAML:2.0:metadata';
const XMLDSIG_NS = 'http://www.w3.org/2000/09/xmldsig#';

const BINDING_HTTP_REDIRECT = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect';
const BINDING_HTTP_POST = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST';

export type SamlMetadataResult = {
    loginUrl: string | null,
    certificate: string | null,
};

function findEntityDescriptor(doc: Document): Element | null {
    const root = doc.documentElement;
    if (root.localName === 'EntityDescriptor') {
        return root;
    }
    if (root.localName === 'EntitiesDescriptor') {
        const entities = root.getElementsByTagNameNS(SAML_MD_NS, 'EntityDescriptor');
        return entities.length > 0 ? entities[0] : null;
    }
    return null;
}

function extractLoginUrl(idpDescriptor: Element): string | null {
    const ssoServices = idpDescriptor.getElementsByTagNameNS(SAML_MD_NS, 'SingleSignOnService');
    if (ssoServices.length === 0) {
        return null;
    }

    for (let i = 0; i < ssoServices.length; i++) {
        if (ssoServices[i].getAttribute('Binding') === BINDING_HTTP_REDIRECT) {
            return ssoServices[i].getAttribute('Location');
        }
    }

    for (let i = 0; i < ssoServices.length; i++) {
        if (ssoServices[i].getAttribute('Binding') === BINDING_HTTP_POST) {
            return ssoServices[i].getAttribute('Location');
        }
    }

    return ssoServices[0].getAttribute('Location');
}

function extractCertificate(idpDescriptor: Element): string | null {
    const keyDescriptors = idpDescriptor.getElementsByTagNameNS(SAML_MD_NS, 'KeyDescriptor');
    if (keyDescriptors.length === 0) {
        return null;
    }

    let targetDescriptor: Element | null = null;
    for (let i = 0; i < keyDescriptors.length; i++) {
        const use = keyDescriptors[i].getAttribute('use');
        if (use === 'signing') {
            targetDescriptor = keyDescriptors[i];
            break;
        }
    }
    if (targetDescriptor == null) {
        targetDescriptor = keyDescriptors[0];
    }

    const certs = targetDescriptor.getElementsByTagNameNS(XMLDSIG_NS, 'X509Certificate');
    if (certs.length === 0) {
        return null;
    }

    const rawCert = (certs[0].textContent ?? '').replace(/\s/g, '');
    if (rawCert.length === 0) {
        return null;
    }

    return `-----BEGIN CERTIFICATE-----\n${rawCert}\n-----END CERTIFICATE-----`;
}

export function parseSamlMetadataXml(xmlString: string): SamlMetadataResult {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    if (doc.querySelector('parsererror')) {
        return {loginUrl: null, certificate: null};
    }

    const entityDescriptor = findEntityDescriptor(doc);
    if (entityDescriptor == null) {
        return {loginUrl: null, certificate: null};
    }

    const idpDescriptors = entityDescriptor.getElementsByTagNameNS(SAML_MD_NS, 'IDPSSODescriptor');
    if (idpDescriptors.length === 0) {
        return {loginUrl: null, certificate: null};
    }

    const idpDescriptor = idpDescriptors[0];

    return {
        loginUrl: extractLoginUrl(idpDescriptor),
        certificate: extractCertificate(idpDescriptor),
    };
}
